import type { Polygon } from 'geojson';

/**
 * A single map feature derived from a Zone document's geoData.
 * Used as the primary data type throughout the Map tab components.
 * id === zone.id (not input.id).
 */
export interface MapFeature {
  /** Zone document ID — used as the Mapbox feature id. */
  id: string;
  /** Zone type bitmask (zone.zoneType). (zoneType & 1) === 1 → Default Zone. */
  zoneType: number;
  /** Raw zone name from the Zone document (zone.zoneName). */
  zoneName: string;
  /** Area from zone.zoneArea (may differ from the polygon's geometric area if
   *  the user has manually overridden it). */
  area: number;
  /** Area unit string (e.g. 'ha') from zone.areaUnit. Null for locally-drafted
   *  features; display code falls back to 'ha'. */
  areaUnit: string | null;
  /** GeoJSON Polygon geometry (zone.geoData). */
  geometry: Polygon;
  /** Paddock ID from the zone's Paddock link (zone.links) — used for tree sync. */
  paddockId: string | null;
  /** Derived display name: paddock link snapLinkName ?? zone.zoneName ?? 'Unnamed'. */
  name: string;
}

/**
 * Creates a locally-drafted MapFeature (draw / merge / split / cutout result).
 * Zone-derived fields default to "no zone" — the feature has not yet been
 * saved to the backend Zone collection, so no Zone document backs it.
 */
export function makeLocalFeature(
  id: string,
  name: string,
  area: number,
  geometry: Polygon
): MapFeature {
  return { id, zoneType: 0, zoneName: '', area, areaUnit: null, geometry, paddockId: null, name };
}
